import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Loader2, FolderTree } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { toast } from '../../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_COLOR = '#FF6B00';
const SWATCHES = ['#FF6B00', '#2563EB', '#16A34A', '#DC2626', '#9333EA', '#0891B2', '#475569'];

const QrProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [color, setColor] = useState(DEFAULT_COLOR);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/qr-projects`);
      setProjects(res.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Could not load projects.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setName('');
    setColor(DEFAULT_COLOR);
    setDialogOpen(true);
  };

  const openEdit = (project) => {
    setEditing(project);
    setName(project.name);
    setColor(project.color);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast({ title: 'Name required', description: 'Give the project a name.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/qr-projects/${editing.id}`, { name, color });
      } else {
        await axios.post(`${API}/qr-projects`, { name, color });
      }
      setDialogOpen(false);
      fetchProjects();
      toast({ title: 'Saved', description: editing ? 'Project updated.' : 'Project created.' });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Could not save this project.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (project) => {
    if (!window.confirm(`Delete "${project.name}"? QR codes in it will become unassigned, not deleted.`)) return;
    setDeletingId(project.id);
    try {
      await axios.delete(`${API}/qr-projects/${project.id}`);
      setProjects((prev) => prev.filter((p) => p.id !== project.id));
      toast({ title: 'Deleted', description: 'Project removed.' });
    } catch (error) {
      toast({ title: 'Error', description: 'Could not delete this project.', variant: 'destructive' });
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-[#FF6B00]" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Projects</h1>
          <p className="text-gray-500 text-sm">Group your QR codes however makes sense to you.</p>
        </div>
        <Button className="bg-[#FF6B00] hover:bg-[#e05f00]" onClick={openCreate}>
          <Plus className="w-4 h-4 mr-2" /> New Project
        </Button>
      </div>

      {projects.length === 0 ? (
        <Card>
          <CardContent className="py-16 flex flex-col items-center text-center">
            <div className="p-4 bg-orange-50 rounded-full mb-4">
              <FolderTree className="w-8 h-8 text-[#FF6B00]" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">No projects yet</h3>
            <p className="text-gray-500 text-sm mb-4">Create one to start grouping your QR codes.</p>
            <Button className="bg-[#FF6B00] hover:bg-[#e05f00]" onClick={openCreate}>
              <Plus className="w-4 h-4 mr-2" /> New Project
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {projects.map((project) => (
            <Card key={project.id}>
              <CardContent className="p-5 flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: project.color }} />
                  <p className="font-medium text-gray-900 truncate">{project.name}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button size="sm" variant="outline" onClick={() => openEdit(project)}>
                    <Edit className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    size="sm" variant="outline" className="text-red-600 hover:bg-red-50"
                    onClick={() => handleDelete(project)}
                    disabled={deletingId === project.id}
                  >
                    {deletingId === project.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Project' : 'New Project'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Storefront Signage" />
            </div>
            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex items-center gap-2 flex-wrap">
                {SWATCHES.map((swatch) => (
                  <button
                    key={swatch}
                    type="button"
                    onClick={() => setColor(swatch)}
                    className={`w-8 h-8 rounded-full border-2 ${color === swatch ? 'border-gray-900' : 'border-transparent'}`}
                    style={{ backgroundColor: swatch }}
                  />
                ))}
                <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="w-8 h-8 rounded border" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-[#FF6B00] hover:bg-[#e05f00]" onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editing ? 'Save Changes' : 'Create Project'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QrProjects;
